import { normaliseImportRow, type DateCodeParser } from "../api/admin";
import type { ImportRow } from "../types/admin";

export const IMPORT_FIELDS = [
  {
    key: "employee_name",
    label: "Employee Name",
    required: true,
    aliases: ["employee", "name", "originator"],
  },
  {
    key: "employee_id",
    label: "Employee ID",
    required: true,
    aliases: ["employee code", "staff id", "employee number"],
  },
  {
    key: "department",
    label: "Department",
    required: true,
    aliases: ["dept", "plant / department"],
  },
  {
    key: "category",
    label: "Category",
    required: true,
    aliases: ["suggestion category"],
  },
  {
    key: "title",
    label: "Title",
    required: true,
    aliases: ["subject", "suggestion title"],
  },
  {
    key: "description",
    label: "Description",
    required: true,
    aliases: ["suggestion", "details", "recommendation"],
  },
  {
    key: "suggestion_number",
    label: "Suggestion #",
    required: false,
    aliases: ["suggestion number", "reference number"],
  },
  {
    key: "status",
    label: "Status",
    required: false,
    aliases: ["state", "progress"],
  },
  {
    key: "created_at",
    label: "Submitted On",
    required: false,
    aliases: ["date", "submitted date", "submission date"],
  },
  {
    key: "submitted_on_behalf_of",
    label: "On Behalf Of",
    required: false,
    aliases: ["beneficiary"],
  },
  {
    key: "other_category",
    label: "Other Category",
    required: false,
    aliases: [],
  },
  { key: "remarks", label: "Remarks", required: false, aliases: ["comments"] },
] as const;
export type ImportField = (typeof IMPORT_FIELDS)[number]["key"];
export type ColumnMapping = Partial<Record<ImportField, string>>;
export interface ImportSheet {
  name: string;
  columns: string[];
  rows: { rowNumber: number; values: Record<string, unknown> }[];
  parseDateCode: DateCodeParser;
}
export const norm = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
export function guessColumns(columns: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  IMPORT_FIELDS.forEach((f) => {
    // Prefer canonical headers before aliases; "Suggestion #" is an identifier,
    // even though punctuation stripping makes it resemble the description alias.
    const canonical = columns.find((c) =>
      [f.key, f.label].some((a) => norm(a) === norm(c)),
    );
    const reserved = IMPORT_FIELDS.filter(
      (other) => other.key !== f.key,
    ).flatMap((other) => [norm(other.key), norm(other.label)]);
    const match =
      canonical ??
      columns.find(
        (c) =>
          !reserved.includes(norm(c)) &&
          f.aliases.some((a) => norm(a) === norm(c)),
      );
    if (match) mapping[f.key] = match;
  });
  return mapping;
}
export async function readImportSheet(file: File): Promise<ImportSheet> {
  if (!/\.xlsx?$/i.test(file.name))
    throw new Error("Choose an .xlsx or .xls workbook.");
  const XLSX = await import("xlsx");
  const book = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const sheet = book.Sheets[book.SheetNames[0]];
  if (!sheet || !sheet["!ref"]) throw new Error("The first sheet is empty.");
  const range = XLSX.utils.decode_range(sheet["!ref"]);
  const columns: string[] = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    const cell = sheet[XLSX.utils.encode_cell({ r: range.s.r, c })];
    const header = String(cell?.v ?? "").trim();
    if (!header)
      throw new Error("Every column in the first row must have a header.");
    if (columns.some((existing) => norm(existing) === norm(header)))
      throw new Error(`Duplicate header: ${header}`);
    columns.push(header);
  }
  const rows: ImportSheet["rows"] = [];
  for (let r = range.s.r + 1; r <= range.e.r; r++) {
    const values: Record<string, unknown> = {};
    columns.forEach((column, i) => {
      const cell = sheet[XLSX.utils.encode_cell({ r, c: range.s.c + i })];
      // Preserve formatted identifiers; retain date serials until mapping identifies date cells.
      values[column] = cell
        ? { raw: cell.v, text: XLSX.utils.format_cell(cell) }
        : { raw: "", text: "" };
    });
    if (
      Object.values(values).some((v) =>
        String((v as { raw: unknown }).raw ?? "").trim(),
      )
    )
      rows.push({ rowNumber: r + 1, values });
  }
  if (!rows.length) throw new Error("The sheet has headers but no data rows.");
  const offset = book.Workbook?.WBProps?.date1904 ? 1462 : 0;
  return {
    name: file.name,
    columns,
    rows,
    parseDateCode: (serial) => XLSX.SSF.parse_date_code(serial + offset),
  };
}
export function mappedValue(
  sheetRow: ImportSheet["rows"][number],
  mapping: ColumnMapping,
  field: ImportField,
): unknown {
  const cell = sheetRow.values[mapping[field] ?? ""] as
    { raw: unknown; text: string } | undefined;
  return field === "created_at" ? (cell?.raw ?? "") : (cell?.text ?? "");
}
export type ValueResolutions = Record<string, string>;
export function resolveImportValue(
  field: "department" | "category" | "status",
  value: string,
  resolutions: ValueResolutions,
  options: { value: string; label: string }[],
): string {
  const explicit = resolutions[`${field}:${value}`];
  if (explicit !== undefined) return explicit;
  if (!value && field === "status") return "submitted";
  return (
    options.find(
      (option) =>
        norm(option.value) === norm(value) ||
        norm(option.label) === norm(value),
    )?.value ?? ""
  );
}

export function buildImportRows(
  sheet: ImportSheet,
  mapping: ColumnMapping,
  resolutions: ValueResolutions,
  options: Record<
    "department" | "category" | "status",
    { value: string; label: string }[]
  >,
): ImportRow[] {
  const rows = sheet.rows.map((source) => {
    const raw: Record<string, unknown> = {};
    IMPORT_FIELDS.forEach((f) => {
      raw[f.key] = mappedValue(source, mapping, f.key);
    });
    const unresolved: string[] = [];
    (["department", "category", "status"] as const).forEach((field) => {
      const value = String(raw[field] ?? "").trim();
      const match = resolveImportValue(
        field,
        value,
        resolutions,
        options[field],
      );
      if (!match) unresolved.push(`Unmatched ${field}: ${value || "(blank)"}`);
      raw[field] = match || value;
    });
    const row = normaliseImportRow(
      raw,
      source.rowNumber - 2,
      sheet.parseDateCode,
    );
    row.errors.push(...unresolved);
    const limits: Partial<Record<ImportField, number>> = {
      suggestion_number: 20,
      employee_name: 255,
      employee_id: 100,
      department: 255,
      category: 100,
      other_category: 100,
      title: 500,
      submitted_on_behalf_of: 255,
    };
    Object.entries(limits).forEach(([key, max]) => {
      if (String(raw[key] ?? "").length > max)
        row.errors.push(`${key} exceeds ${max} characters`);
    });
    return row;
  });
  const numbers = new Map<string, number>();
  rows.forEach((row) => {
    if (row.suggestion_number)
      numbers.set(
        row.suggestion_number,
        (numbers.get(row.suggestion_number) ?? 0) + 1,
      );
  });
  rows.forEach((row) => {
    if (row.suggestion_number && (numbers.get(row.suggestion_number) ?? 0) > 1)
      row.errors.push("Duplicate suggestion number in file");
  });
  return rows;
}
export async function downloadImportTemplate(statuses: string[]) {
  const XLSX = await import("xlsx");
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    book,
    XLSX.utils.aoa_to_sheet([IMPORT_FIELDS.map((f) => f.label)]),
    "Suggestions",
  );
  XLSX.utils.book_append_sheet(
    book,
    XLSX.utils.aoa_to_sheet([
      ["Column", "What to enter"],
      [
        "Status",
        `Optional; blank means submitted. Allowed: ${statuses.join(", ")}`,
      ],
      ["Submitted On", "Optional YYYY-MM-DD or Excel date cell."],
      ["Employee ID", "Format as Text to preserve leading zeros."],
      ["Other Category", "Required when Category is Others."],
      [
        "Suggestion #",
        "Optional; existing numbers are rejected. Blank generates a number.",
      ],
    ]),
    "Guide",
  );
  XLSX.writeFile(book, "ess-historical-import-template.xlsx");
}
